import { Text } from '@wistia/vhs';
import { MemoizedDangerouslyRenderedHtml } from '~/components/MemoizedDangerouslyRendered';
import type { JSX } from 'react';
import { LetterBox, StuffBelowMediaContainer, TitleAndDescriptionContainer } from './Shared';
import type { AnonymousMediaQuery } from '../__gql/AnonymousMedia.gql.gen';
import { TabbedArea } from '../TabbedArea';

type ImageLayoutProps = {
  commentingDisabled: boolean;
  media: NonNullable<AnonymousMediaQuery['anonymousMedia']>;
  imageStyleOverrides?: React.CSSProperties;
};

export const ImageLayout = ({
  commentingDisabled,
  media,
  imageStyleOverrides,
}: ImageLayoutProps): JSX.Element => {
  const {
    topLevelAudienceComments,
    topLevelTeamComments,
    id: mediaId,
    hashedId,
    name,
    description,
    publicCommentsSelection,
    imageUrl,
  } = media;

  return (
    <>
      <LetterBox>
        <img
          alt={name}
          data-testid="media-image"
          // Since we know the media type is Image, we know there's an imageUrl.
          // This hackily informs TypeScript of that fact.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          src={imageUrl!}
          style={{
            display: 'block',
            position: 'relative',
            margin: '0 auto',
            maxHeight: '80vh',
            maxWidth: '100%',
            ...imageStyleOverrides,
          }}
        />
      </LetterBox>
      <StuffBelowMediaContainer>
        <TitleAndDescriptionContainer>
          <Text
            renderAs="h1"
            variant="headline1"
          >
            {name}
          </Text>
          {description != null && <MemoizedDangerouslyRenderedHtml html={description} />}
        </TitleAndDescriptionContainer>
        <TabbedArea
          audienceComments={topLevelAudienceComments}
          commentingDisabled={commentingDisabled}
          teamComments={topLevelTeamComments}
          mediaId={mediaId}
          mediaHashedId={hashedId}
          shouldShowTranscript={false}
          videoEmbed={null}
          publicCommentsSelection={publicCommentsSelection}
        />
      </StuffBelowMediaContainer>
    </>
  );
};
